/**
 * useFeatureFlags Hook
 *
 * Custom hooks for feature flag management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

type FeatureFlags = Record<string, boolean>;

// Get all feature flags (admin only)
async function fetchFeatureFlags(): Promise<FeatureFlags> {
  const response = await fetch(`${API_BASE_URL}/config/features`, {
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin-Berechtigung erforderlich');
    }
    throw new Error('Fehler beim Laden der Funktionen');
  }
  return response.json();
}

export function useFeatureFlags(enabled = true) {
  return useQuery({
    queryKey: ['feature-flags'],
    queryFn: fetchFeatureFlags,
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
}

// Check if specific feature is enabled (any authenticated user)
async function checkFeatureEnabled(flag: string): Promise<boolean> {
  const response = await fetch(`${API_BASE_URL}/config/features/${flag}/enabled`, {
    credentials: 'include',
  });
  if (!response.ok) {
    return false;
  }
  return response.json();
}

export function useIsFeatureEnabled(flag: string, enabled = true) {
  return useQuery({
    queryKey: ['feature-flag', flag],
    queryFn: () => checkFeatureEnabled(flag),
    enabled: enabled && !!flag,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Set feature flag (admin only)
async function setFeatureFlag({
  flag,
  enabled,
}: {
  flag: string;
  enabled: boolean;
}): Promise<FeatureFlags> {
  const response = await fetch(`${API_BASE_URL}/config/features/${flag}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Fehler beim Speichern');
  }
  return response.json();
}

export function useSetFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['feature-flag'] });
    },
  });
}

// Helper hook that provides isEnabled function
export function useFeatureFlagsHelper() {
  const { data: flags, isLoading } = useFeatureFlags();

  const isEnabled = (flag: string): boolean => {
    if (!flags) return false;
    return flags[flag] === true;
  };

  return {
    flags,
    isLoading,
    isEnabled,
  };
}
